<template>
  <div id="app">
    <header class="app-header">
      <div class="brand-block">
        <p class="eyebrow">LotLedger</p>
        <h1>Wine Bar</h1>
      </div>

      <nav class="main-nav">
        <router-link to="/home" class="nav-button">Home</router-link>
        <router-link to="/auctions" class="nav-button">Purchases</router-link>
        <router-link to="/sales" class="nav-button">Sales</router-link>
        <router-link to="/inventory" class="nav-button">Inventory</router-link>
        <router-link to="/expenses" class="nav-button">Expenses</router-link>
        <router-link to="/reports-dashboard" class="nav-button">Reports</router-link>
        <router-link to="/items" class="nav-button">Items</router-link>
        <router-link to="/platforms" class="nav-button">Distributors</router-link>
        <router-link to="/food-admin" class="nav-button">Food Admin</router-link>
        <router-link to="/cash-control" class="nav-button">Cash Control</router-link>
        <router-link to="/admin" class="nav-button">Admin</router-link>
      </nav>

      <button
        v-if="isAuthenticated"
        @click="logout"
        class="logout-button"
      >
        Logout
      </button>
    </header>

    <main class="app-main">
      <router-view />
    </main>

    <PabloAssistant />
  </div>
</template>

<script>
import PabloAssistant from './components/PabloAssistant.vue';

export default {
  name: 'App',
  components: {
    PabloAssistant
  },
  data() {
    return {
      isAuthenticated: sessionStorage.getItem('authenticated') === 'true'
    };
  },
  methods: {
    logout() {
      sessionStorage.removeItem('authenticated');
      this.isAuthenticated = false;
      this.$router.push('/');
    },
    updateAuthStatus() {
      this.isAuthenticated = sessionStorage.getItem('authenticated') === 'true';
    }
  },
  mounted() {
    window.addEventListener('storage', this.updateAuthStatus);
  },
  beforeUnmount() {
    window.removeEventListener('storage', this.updateAuthStatus);
  }
};
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800;900&family=Poppins:wght@300;400;600;800&display=swap');

:root {
  --wine-black: #0d090d;
  --wine-charcoal: #171016;
  --wine-plum: #24141b;
  --wine-red: #8f123f;
  --wine-orange: #c24a2f;
  --wine-gold: #c8a66a;
  --wine-cream: #f7efe8;
  --wine-muted: #cfc2bd;
  --wine-dim: #8f827e;
}

* {
  box-sizing: border-box;
}

html,
body {
  margin: 0;
  min-height: 100%;
  background:
    radial-gradient(circle at top left, rgba(128, 18, 54, 0.38), transparent 34%),
    radial-gradient(circle at bottom right, rgba(194, 74, 47, 0.16), transparent 32%),
    linear-gradient(135deg, var(--wine-black) 0%, var(--wine-charcoal) 42%, var(--wine-plum) 100%);
}

body {
  color: var(--wine-cream);
  font-family: "Inter", "Poppins", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
}

#app {
  min-height: 100vh;
  color: var(--wine-cream);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* ---------- App Header ---------- */

.app-header {
  position: sticky;
  top: 0;
  z-index: 1000;
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 18px;
  padding: 18px 24px;
  background:
    linear-gradient(135deg, rgba(13, 9, 13, 0.92), rgba(23, 16, 22, 0.82));
  border-bottom: 1px solid rgba(255, 255, 255, 0.09);
  backdrop-filter: blur(18px);
  box-shadow: 0 18px 48px rgba(0, 0, 0, 0.28);
}

.brand-block {
  min-width: 170px;
}

.eyebrow {
  margin: 0 0 4px;
  color: var(--wine-gold);
  text-transform: uppercase;
  letter-spacing: 0.18em;
  font-size: 11px;
  font-weight: 900;
}

.brand-block h1 {
  margin: 0;
  color: var(--wine-cream);
  font-size: 26px;
  line-height: 0.95;
  letter-spacing: -0.055em;
  font-weight: 900;
}

/* ---------- Navigation ---------- */

.main-nav {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
}

.nav-button {
  min-height: 42px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 15px;
  border-radius: 999px;
  color: var(--wine-muted);
  text-decoration: none;
  font-size: 14px;
  font-weight: 800;
  border: 1px solid transparent;
  background: rgba(255, 255, 255, 0.045);
  transition:
    transform 0.14s ease,
    background 0.14s ease,
    color 0.14s ease,
    border-color 0.14s ease,
    box-shadow 0.14s ease;
}

.nav-button:hover {
  color: var(--wine-cream);
  background: rgba(255, 255, 255, 0.085);
  border-color: rgba(255, 255, 255, 0.11);
  transform: translateY(-1px);
}

.nav-button.router-link-active,
.nav-button.router-link-exact-active {
  color: #fff8f1;
  background: linear-gradient(135deg, rgba(143, 18, 63, 0.95), rgba(194, 74, 47, 0.88));
  border-color: rgba(255, 255, 255, 0.14);
  box-shadow: 0 12px 28px rgba(194, 74, 47, 0.22);
}

/* ---------- Logout ---------- */

.logout-button {
  min-height: 42px;
  padding: 0 18px;
  border-radius: 999px;
  border: 1px solid rgba(255, 118, 118, 0.25);
  background: rgba(155, 45, 45, 0.34);
  color: #ffb4a8;
  cursor: pointer;
  font-weight: 900;
  font-size: 14px;
  transition:
    transform 0.14s ease,
    box-shadow 0.14s ease,
    background 0.14s ease;
}

.logout-button:hover {
  background: rgba(155, 45, 45, 0.48);
  transform: translateY(-1px);
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.24);
}

/* ---------- Main Content ---------- */

.app-main {
  min-height: calc(100vh - 80px);
}

/* This helps older pages not look boxed-in against the dark shell */
.app-main > * {
  text-align: left;
}

/* ---------- Responsive ---------- */

@media (max-width: 1100px) {
  .app-header {
    grid-template-columns: 1fr auto;
  }

  .brand-block {
    grid-column: 1;
  }

  .logout-button {
    grid-column: 2;
    grid-row: 1;
  }

  .main-nav {
    grid-column: 1 / -1;
    justify-content: flex-start;
  }
}

@media (max-width: 720px) {
  .app-header {
    padding: 14px;
    gap: 12px;
  }

  .brand-block h1 {
    font-size: 23px;
  }

  .main-nav {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    width: 100%;
  }

  .nav-button,
  .logout-button {
    width: 100%;
  }

  .logout-button {
    grid-column: 1 / -1;
    grid-row: auto;
  }
}
</style>